import React from 'react';
import { Card, CardContent, CardActions, Typography, IconButton, Box, alpha, Chip, Button } from '@mui/material'
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'; // LAP-733 (GE022) round 10 fix-up
import { goToButtonTarget } from './publicationHandlers';
import {
  BarChartComponent,
  LineChartComponent,
  DonutChartComponent,
  StackedBarChartComponent,
} from './SummaryCharts';
import SmartCardChart from './SmartCardChart';
import { getDomainColor } from './publicationHandlers';
import { getTranslation, getUserLanguage } from './translations'; // LAP-733 (GE022) round 10 fix-up

// LAP-733 (GE022) round 10 fix-up — map server-side publication.type to i18n key
const PUBLICATION_TYPE_KEY = {
  Insight: 'publication',
  Publication: 'publication',
  Summary: 'summary',
  Reminder: 'reminder',
  Actionable: 'actionable',
  News: 'news',
};

const SummaryPublicationCard = ({ publication, onInteraction, showArchivedIndicators = false }) => {
  const navigate = useNavigate();

  // LAP-733 (GE022) round 10 fix-up — resolve user language + BCP-47 locale
  const siteUser = useSelector((store) => store.user);
  const lang = getUserLanguage(siteUser);
  const dateLocale = ({ fr: 'fr-FR', en: 'en-GB', de: 'de-DE', it: 'it-IT', es: 'es-ES' })[lang] || lang || 'fr-FR';

  // LAP-733 (GE022) round 10 fix-up — translate the publication.type badge
  const displayType = getTranslation(PUBLICATION_TYPE_KEY[publication.type] || 'publication', lang);

  const handleLike = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'like');
  };

  const handleSave = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'save');
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'dismiss');
  };

  const handleCardClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Navigate/open link first (same user gesture) so window.open isn't blocked
    goToButtonTarget(publication.button_target, navigate, publication);
    onInteraction(publication.id, 'click');
  };

  const hasButton = (publication.button_text || publication.button_target) && publication.button_target;

  const getIcon = (icon) => {
    return icon || '📊';
  };

  const renderChart = () => {
    if (!publication.graph_config) return null;

    // Use SmartCardChart with domain color for consistent chart rendering
    const domainColor = getDomainColor(publication.domain);
    return (
      <SmartCardChart 
        config={publication.graph_config} 
        color={domainColor}
        domain={publication.domain}
      />
    );
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-4px)',
        },
      }}
    >
      <CardContent>
        {/* Header: Icon + Title + Subtitle */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="start" gap={1}>
            <Typography
              variant="h4"
              component="span"
              sx={{
                fontSize: '2rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              }}
            >
              {publication.icon || '📋'}
            </Typography>

            <Box>
              {/* Title */}
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.4,
                  // mb: 1,
                  color: '#E0E0E0',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textAlign: 'left',
                }}
              >
                {publication.title}
              </Typography>

              {/* Subtitle */}
              {publication.subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#E0E0E0', 0.7),
                    fontSize: '0.85rem',
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {publication.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={handleDismiss} sx={{ ml: 1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Image */}
        {publication.image_url && (
          <Box
            sx={{
              width: '100%',
              borderRadius: '12px',
              overflow: 'hidden',
              mb: 2,
              mt: 1,
            }}
          >
            <img
              src={publication.image_url}
              alt={publication.title}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'cover',
                maxHeight: '300px',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}

        {/* Dynamic Text (text1 or text2 based on result) */}
        {publication.text && (
          <Typography
            variant="body2"
            sx={{
              color: alpha('#E0E0E0', 0.8),
              fontSize: '0.875rem',
              lineHeight: 1.6,
              mb: 2,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}

          >
            {publication.text}
          </Typography>
        )}

        {/* Chart Visualization */}
        {publication.graph_config && <Box sx={{ mt: 2, mb: 1 }}>{renderChart()}</Box>}

        {/* Action button when button_text or button_target is defined */}
        {hasButton && (
          <Button
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCardClick(e);
            }}
            fullWidth
            sx={{
              mt: 2,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: '#fff',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              py: 1.25,
            }}
          >
            {publication.button_text || 'Voir'}
          </Button>
        )}
      </CardContent>

      {/* Actions: Like, Save, Date */}
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box>
          <IconButton onClick={handleLike} color={publication.was_liked ? 'error' : 'default'}>
            {publication.was_liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </IconButton>
          <IconButton onClick={handleSave} color={publication.is_saved ? 'primary' : 'default'}>
            {publication.is_saved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {/* Type Chip */}
          <Chip
            label={displayType}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: '20px',
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
          {/* Show saved/liked indicators in archived view */}
          {showArchivedIndicators && (
            <>
              {publication.is_saved && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: alpha('#8b5cf6', 0.2),
                    color: '#8b5cf6',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '4px',
                    fontSize: '0.65rem',
                  }}
                >
                  <BookmarkIcon sx={{ fontSize: '0.7rem' }} />
                  Saved
                </Box>
              )}
              {publication.was_liked && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: alpha('#ef4444', 0.2),
                    color: '#ef4444',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '4px',
                    fontSize: '0.65rem',
                  }}
                >
                  <FavoriteIcon sx={{ fontSize: '0.7rem' }} />
                  Liked
                </Box>
              )}
            </>
          )}
          <Typography variant="caption" color="text.secondary">
            {new Date(publication.datetime_displayed).toLocaleDateString(dateLocale) /* LAP-733 (GE022) round 10 fix-up */}
          </Typography>
        </Box>
      </CardActions>
    </Card>
  )
};

export default SummaryPublicationCard;
